import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useClientStore } from '../../stores/clientStore';
import { useTemplateStore } from '../../stores/templateStore';
import { useCanUseFeature } from '../../stores/subscriptionStore';
import { clientsApi } from '../../api/clients';
import { useToast } from '../ui/Toaster';
import type { ClientWithPortal } from '@affogato/shared';
import clsx from 'clsx';
import {
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (client: ClientWithPortal) => void;
}

export function CreateClientModal({ isOpen, onClose, onCreated }: CreateClientModalProps) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { addClient } = useClientStore();
  const { templates, fetchTemplates, applyTemplate } = useTemplateStore();
  const clientQuota = useCanUseFeature('clients');
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientQuota.allowed) {
      toast.error('Has alcanzado el límite de clientes de tu plan. Actualiza tu plan para continuar.');
      return;
    }

    if (!validate()) return;

    setIsLoading(true);
    try {
      const response = await clientsApi.create({
        name: name.trim(),
        company: company.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      });

      addClient(response.client);

      // Apply template if selected
      if (selectedTemplateId) {
        try {
          const result = await applyTemplate(selectedTemplateId, { clientId: response.client.id });
          toast.success(`Cliente creado con ${result.foldersCreated} carpetas`);
        } catch {
          toast.success('Cliente creado (error al aplicar plantilla)');
        }
      } else {
        toast.success('Cliente creado');
      }

      onCreated(response.client);
      handleClose();
    } catch (error: any) {
      toast.error(error.message || 'Error al crear cliente');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setCompany('');
    setEmail('');
    setPhone('');
    setSelectedTemplateId(null);
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Client"
      description="Create a client to share files through a dedicated portal"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Client Name"
          placeholder="John Doe"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          leftIcon={<UserIcon className="w-5 h-5" />}
          autoFocus
        />

        <Input
          label="Company"
          placeholder="Acme Inc. (optional)"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          leftIcon={<BuildingOfficeIcon className="w-5 h-5" />}
        />

        <Input
          label="Email"
          type="email"
          placeholder="client@company.com (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          leftIcon={<EnvelopeIcon className="w-5 h-5" />}
        />

        <Input
          label="Phone"
          placeholder="+1 234 567 8900 (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          leftIcon={<PhoneIcon className="w-5 h-5" />}
        />

        {/* Template Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <div className="flex items-center gap-2">
              <FolderIcon className="w-5 h-5" />
              Plantilla de carpetas (opcional)
            </div>
          </label>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            <button
              type="button"
              onClick={() => setSelectedTemplateId(null)}
              className={clsx(
                'w-full p-3 rounded-lg border text-left text-sm transition-colors',
                selectedTemplateId === null
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
              )}
            >
              <span className="text-gray-700 dark:text-slate-300">Sin plantilla</span>
            </button>
            {templates.map((template) => (
              <button
                type="button"
                key={template.id}
                onClick={() => setSelectedTemplateId(template.id)}
                className={clsx(
                  'w-full p-3 rounded-lg border text-left text-sm transition-colors',
                  selectedTemplateId === template.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                )}
              >
                <span className="font-medium text-gray-900 dark:text-white">{template.name}</span>
                {template.description && (
                  <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    {template.description}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {!clientQuota.allowed && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Has alcanzado el límite de {clientQuota.limit} clientes de tu plan. Actualiza tu plan para crear más clientes.
            </p>
          </div>
        )}
        {clientQuota.allowed && clientQuota.remaining !== null && (
          <p className="text-xs text-gray-500 dark:text-slate-400">
            {clientQuota.remaining} de {clientQuota.limit} clientes restantes
          </p>
        )}

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={!clientQuota.allowed}
            className="flex-1"
          >
            Crear Cliente
          </Button>
        </div>
      </form>
    </Modal>
  );
}
